import React, { useState, useEffect } from 'react';
import GeneralQuizQuestion from './GeneralArithmeticQuizQuestion';
import './GeneralArithmeticQuiz.css';
import { Icon } from '@iconify/react';

// Function to generate a random mixed-operation question
const generateMixedOperationQuestion = () => {
  const operations = [
    { symbol: '+', method: (a, b) => a + b },
    { symbol: '-', method: (a, b) => a - b },
    { symbol: '*', method: (a, b) => a * b },
    { symbol: '/', method: (a, b) => (b !== 0 ? (a / b).toFixed(2) : 'undefined') },
  ];

  // Generate random numbers
  const numbers = Array.from({ length: 4 }, () => Math.floor(Math.random() * 10) + 1);
  const selectedOperations = [];
  
  // Randomly select three operations
  for (let i = 0; i < 3; i++) {
    selectedOperations.push(operations[Math.floor(Math.random() * operations.length)]);
  }

  // Construct the question string with a mix of operations
  let question = `${numbers[0]}`;
  for (let i = 0; i < selectedOperations.length; i++) {
    question += ` ${selectedOperations[i].symbol} ${numbers[i + 1]}`;
  }

  // Calculate the correct answer
  let correctAnswer = numbers[0];
  for (let i = 0; i < selectedOperations.length; i++) {
    correctAnswer = selectedOperations[i].method(correctAnswer, numbers[i + 1]);
  }

  // Create possible answers (correct and wrong)
  const answers = [correctAnswer.toString()];
  while (answers.length < 3) {
    const wrongAnswer = (Math.random() * 20).toFixed(2);
    if (!answers.includes(wrongAnswer)) {
      answers.push(wrongAnswer);
    }
  }

  // Shuffle answers
  const shuffledAnswers = answers.sort(() => Math.random() - 0.5);

  return {
    question: question,
    answers: shuffledAnswers,
    correct: correctAnswer.toString(),
  };
};

function GeneralArithmeticQuiz() {
  const [currentQuestion, setCurrentQuestion] = useState(generateMixedOperationQuestion());
  const [score, setScore] = useState(0);
  const [questionsAnswered, setQuestionsAnswered] = useState(0);
  const [scoreboard, setScoreboard] = useState([]);
  const [showScoreboard, setShowScoreboard] = useState(false);
  const [time, setTime] = useState(0);
  const [isRunning, setIsRunning] = useState(false);

  const handleAnswer = (selectedAnswer) => {
    const isCorrect = selectedAnswer.toString() === currentQuestion.correct;

    if (isCorrect) {
      setScore(score + 1);
    }

    setQuestionsAnswered(questionsAnswered + 1);

    // Add the current question and result to the scoreboard
    setScoreboard([
      {
        questionNumber: questionsAnswered + 1,
        question: currentQuestion.question,
        selectedAnswer: selectedAnswer,
        correctAnswer: currentQuestion.correct,
        isCorrect: isCorrect,
      },
      ...scoreboard,
    ]);

    // Generate a new random question
    setCurrentQuestion(generateMixedOperationQuestion());
  };

  const startStopwatch = () => setIsRunning(true);
  const pauseStopwatch = () => setIsRunning(false);
  const resetStopwatch = () => {
    setIsRunning(false);
    setTime(0);
  };

  useEffect(() => {
    let timer;
    if (isRunning) {
      timer = setInterval(() => {
        setTime((prevTime) => prevTime + 1);
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [isRunning]);

  const formatTime = (timeInSeconds) => {
    const hours = Math.floor(timeInSeconds / 3600);
    const minutes = Math.floor((timeInSeconds % 3600) / 60);
    const seconds = timeInSeconds % 60;
    return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const calculatePercentage = () => {
    return questionsAnswered === 0 ? 0 : ((score / questionsAnswered) * 100).toFixed(2);
  };

  const correctIconStyle = {
    color: '#32cd32',
    marginRight: '5px',
    verticalAlign: 'middle',
  };

  const incorrectIconStyle = {
    color: '#ff0000',
    marginRight: '5px',
    verticalAlign: 'middle',
  };

  const btnStyle = {
    margin: '5px',
    padding: '4px 8px',
    fontSize: '12px',
    border: 'none',
    borderRadius: '5px',
    backgroundColor: '#4f4f4f',
    color: 'white',
    cursor: 'pointer'
  };

  return (
    <div className="quiz-container">
      <h3>Question: {questionsAnswered}</h3>
      <GeneralQuizQuestion
        question={currentQuestion}
        handleAnswer={handleAnswer}
      />

      <div className="stopwatch-container">
        <h3>Time: {formatTime(time)}</h3>
        {isRunning ? (
          <button onClick={pauseStopwatch} style={btnStyle} >Pause</button>
        ) : (
          <button onClick={startStopwatch} style={btnStyle}>Start</button>
        )}
        <button onClick={resetStopwatch} style={btnStyle}>Reset</button>
      </div>

      <button onClick={() => setShowScoreboard(!showScoreboard)} style={btnStyle}>
        {showScoreboard ? 'Hide Scoreboard' : 'Show Scoreboard'}
      </button>

      {showScoreboard && (
        <div className="scoreboard-container">
          <h3>Score: {score}/{questionsAnswered} ({calculatePercentage()}%)</h3>
          <h4>Scoreboard</h4>
          <ul>
            {scoreboard.map((entry, index) => (
              <li key={index}>
                <span>{entry.questionNumber}. {entry.question}</span>
                <span> - Your Answer: {entry.selectedAnswer}</span>
                <span> - Correct Answer: {entry.correctAnswer}</span>
                <span> - {entry.isCorrect ? (
                  <Icon icon="icon-park-solid:correct" width="24" height="24" style={correctIconStyle} />
                ) : (
                  <Icon icon="icomoon-free:cross" width="24" height="24" style={incorrectIconStyle} />
                )}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default GeneralArithmeticQuiz;
